import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import StorefrontLayout from "@/components/StorefrontLayout";
import api, { productImage } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Check, Loader2, Download, Copy, Sparkles } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 8;

export default function OrderConfirmation() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "checking" : "ok");
  const [info, setInfo] = useState(null);
  const [order, setOrder] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [copied, setCopied] = useState(false);
  const { clear } = useCart();

  // Clear the cart exactly once when the payment is confirmed (or the legacy
  // no-Stripe order lands on this page). This preserves the cart if the user
  // cancels or the payment fails so they can retry without re-adding items.
  useEffect(() => {
    if (status === "paid" || status === "ok") clear();
  }, [status, clear]);

  // Fetch order details (for downloads)
  useEffect(() => {
    if (!id) return;
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [id, status]);

  // Fetch upsell when payment confirmed (or when the page first loads for a non-Stripe order)
  useEffect(() => {
    if (!id) return;
    if (status !== "paid" && status !== "ok") return;
    api.get(`/orders/${id}/upsell`).then((r) => setUpsell(r.data)).catch(() => {});
  }, [id, status]);

  // Poll Stripe status if we came back from Stripe checkout
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let stop = false;
    const poll = async () => {
      if (stop || attempts >= MAX_POLLS) {
        if (!stop) setStatus("timeout");
        return;
      }
      attempts += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setInfo(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
      } catch (e) { console.debug("checkout status poll error", e?.message); }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  // Only recompute the downloadable-items subset when the order actually changes.
  const downloadableItems = useMemo(
    () => (order?.items || []).filter((it) => it.download_url),
    [order],
  );
  const showDownloads = (status === "paid" || status === "ok") && downloadableItems.length > 0;

  return (
    <StorefrontLayout>
      <section className="max-w-[1320px] mx-auto px-6 sm:px-10 py-20">
        <div className="bx p-10 max-w-2xl">
          {status === "checking" && (
            <>
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--accent)" }} />
              <div data-testid="order-status" className="label-mono mt-4">/ VERIFYING PAYMENT</div>
              <h1 className="font-display text-4xl font-medium tracking-tight mt-2">One moment…</h1>
              <p className="mt-3 text-base" style={{ color: "var(--ink-soft)" }}>We're confirming your payment with Stripe.</p>
            </>
          )}
          {(status === "ok" || status === "paid") && (
            <>
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                   style={{ background: "var(--paper-2)", color: "var(--accent)" }}>
                <Check className="w-7 h-7" />
              </div>
              <div data-testid="order-status" className="label-mono mb-3">
                {status === "paid" ? "/ PAYMENT CONFIRMED" : "/ ORDER PLACED"}
              </div>
              <h1 className="font-display text-5xl font-medium tracking-tight">
                Thank you<span style={{ color: "var(--accent)" }}>.</span>
              </h1>
              <p className="mt-4 text-base" style={{ color: "var(--ink-soft)" }}>Your order is in. Downloads below — physical items ship in 2–3 business days.</p>
              <div data-testid="order-id" className="mt-4 font-mono-ui text-xs px-3 py-2 inline-block rounded"
                   style={{ background: "var(--paper-2)" }}>{id}</div>
              {info?.amount_total != null && (
                <div className="mt-2 font-mono-ui text-sm">Total charged: <strong>${(info.amount_total / 100).toFixed(2)}</strong> {(info.currency || "").toUpperCase()}</div>
              )}
            </>
          )}
          {(status === "expired" || status === "timeout") && (
            <>
              <div data-testid="order-status" className="label-mono mb-3" style={{ color: "var(--accent)" }}>/ PAYMENT NOT CONFIRMED</div>
              <h1 className="font-display text-4xl font-medium tracking-tight">Hmm.</h1>
              <p className="mt-3 text-base" style={{ color: "var(--ink-soft)" }}>We couldn't confirm the payment in time. Check your email — if you were charged you'll get a receipt; otherwise try again.</p>
            </>
          )}
          <div className="mt-8 flex gap-3">
            <Link to="/shop" className="btn-pill">Keep browsing →</Link>
            <Link to="/" className="btn-ghost">Home</Link>
          </div>
        </div>

        {/* Downloads panel */}
        {showDownloads && (
          <div className="mt-8 bx p-8 max-w-2xl" data-testid="downloads-panel">
            <div className="label-mono mb-3" style={{ color: "var(--accent)" }}>/ YOUR DOWNLOADS</div>
            <h2 className="font-display text-2xl font-medium tracking-tight mb-4">Files ready for you.</h2>
            <ul className="space-y-3">
              {downloadableItems.map((it) => (
                <li key={it.product_id} data-testid={`download-${it.product_id}`}
                    className="flex items-center justify-between gap-3 rule-t pt-3">
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{it.title}</div>
                    <div className="label-mono mt-0.5">{it.category || "Digital"}</div>
                  </div>
                  <a
                    href={it.download_url} target="_blank" rel="noreferrer"
                    className="btn-pill flex-shrink-0"
                  >
                    <Download className="w-4 h-4" /> Download
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs" style={{ color: "var(--ink-mute)" }}>
              These links stay live. Bookmark this page or check your email — we'll send it there too.
            </p>
          </div>
        )}

        {/* Post-purchase upsell */}
        {upsell && upsell.picks && upsell.picks.length > 0 && (
          <div className="mt-8 bx p-8 max-w-3xl" data-testid="upsell-panel" style={{ background: "var(--paper-2)" }}>
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
              <div>
                <div className="label-mono mb-1" style={{ color: "var(--accent)" }}>/ ONE MORE THING</div>
                <h2 className="font-display text-3xl font-medium tracking-tight">Complete the collection.</h2>
                <p className="text-sm mt-2" style={{ color: "var(--ink-soft)" }}>
                  Use the code below for <strong>{upsell.coupon.discount_pct}% off</strong> a repeat order — good for the next 48 hours.
                </p>
              </div>
              <button
                data-testid="upsell-copy-code"
                onClick={() => {
                  navigator.clipboard.writeText(upsell.coupon.code);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2500);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-mono-ui"
                style={{ background: "var(--ink)", color: "var(--paper)" }}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {upsell.coupon.code}
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 mt-6">
              {upsell.picks.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} data-testid={`upsell-pick-${p.id}`} className="product-card group block">
                  <div className="relative aspect-[4/5] overflow-hidden bx" style={{ background: "var(--paper)" }}>
                    <img src={productImage(p)} alt={p.title} className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.04]" />
                  </div>
                  <div className="mt-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-base font-medium truncate">{p.title}</div>
                      <div className="label-mono mt-0.5">{p.category}</div>
                    </div>
                    <div className="font-mono-ui text-sm">${p.price.toFixed(2)}</div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2 text-xs" style={{ color: "var(--ink-soft)" }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} /> Apply {upsell.coupon.code} at checkout — stacks with the bundle discount.
            </div>
          </div>
        )}
      </section>
    </StorefrontLayout>
  );
}
